import { useRouter } from "next/router";
import useSWR from "swr";

import { useCallback, useMemo } from "react";

import { differenceInWeeks, format } from "date-fns";
import { twMerge } from "tailwind-merge";

import { faTimes } from "@fortawesome/free-solid-svg-icons";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import Modal from "components/Modal";
import { regulatoryQuestionnaireSubmissionUrl } from "components/ToolRegulatoryQuestionnaire/Form";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { baseFetcher } from "utils/baseFetcher";

export default function RegulatoryQuarterCheck() {
  const router = useRouter();
  const { account } = useAuthentication();
  const { trackActivity } = useActivityTracker();

  const { data: submission, isValidating: isValidatingSubmission } = useSWR(
    account?.id && account?.isInsightProvider
      ? regulatoryQuestionnaireSubmissionUrl(account?.id)
      : null,
    baseFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const onOpenAgreement = useCallback(() => {
    trackActivity("ip_quarter_regulatory_check.view", account, {
      date: format(new Date(), "dd MMM yyy"),
    });
  }, [account, trackActivity]);

  const onCloseAgreement = useCallback(() => {
    router.push("/tools/regulatory-process-and-conflicts-questionnaire");
  }, [router]);

  const hideModal = useMemo(() => {
    return (
      !account ||
      !account?.canAccessRegulatoryForm ||
      isValidatingSubmission ||
      router?.pathname ===
        "/tools/regulatory-process-and-conflicts-questionnaire" ||
      (submission &&
        differenceInWeeks(
          new Date(),
          new Date(submission?.hubspotFormSubmittedAt)
        ) <= 15)
    );
  }, [account, isValidatingSubmission, router?.pathname, submission]);

  if (hideModal) {
    return null;
  }

  return (
    <Modal
      className={twMerge("md:w-[600px]")}
      overlayClassName="bg-black/90 flex flex-col md:justify-center"
      onClose={onCloseAgreement}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      customBackButtonLabel=" "
      onAfterOpen={() => {
        document.body.classList.add("!max-h-[calc(100vh-100px)]");
        onOpenAgreement();
      }}
      onAfterClose={() => {
        document.body.classList.remove("!max-h-[calc(100vh-100px)]");
      }}
    >
      <div className={twMerge("p-[15px] md:p-[30px]")}>
        <h1 className="text-[20px] font-bold flex items-center justify-between">
          <span className="flex-1">
            Important: Regulatory Submission Refresh
          </span>
          <div
            className="bg-inactive-gray/20 flex md:hidden items-center justify-center rounded-full hover:bg-inactive-gray w-[40px] h-[40px]"
            onClick={onCloseAgreement}
          >
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </h1>
        <p>
          To ensure your regulatory status information is up to date, please
          refresh your responses to our regulatory form before utilizing the
          platform.
        </p>
        <button
          className={twMerge(
            "bg-smart-teal hover:bg-dark-teal flex-1",
            "border-none rounded-full",
            "text-white font-medium",
            "leading-[1] md:leading-[24px]",
            "text-[15px]",
            "px-[1.25em] py-[0.75em] md:px-[1em] md:py-[0.5em]"
          )}
          onClick={onCloseAgreement}
        >
          Update Now
        </button>
      </div>
    </Modal>
  );
}
